import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactElement,
} from "react";
import { useChat } from "@ai-sdk/react";
import {
	DefaultChatTransport,
	lastAssistantMessageIsCompleteWithToolCalls,
} from "ai";
import {
	FiX,
	FiPlus,
	FiSend,
	FiTerminal,
	FiClock,
	FiChevronDown,
} from "react-icons/fi";
import { SiAnthropic, SiOpenai, SiGooglegemini } from "react-icons/si";
import { Menu } from "@base-ui/react/menu";
import { useSession } from "@baseline/web/auth";
import { CHAT_API_URL, getChatHeaders } from "../lib/chatApi";
import { usePageContext, hasContextDrifted } from "../hooks/usePageContext";
import { useChatThread } from "../hooks/useChatThread";
import { ChatMessage } from "./ChatMessage";
import { ChatContextPill } from "./ChatContextPill";

interface ChatSidebarProps {
	isOpen: boolean;
	onClose: () => void;
}

function formatRelativeTime(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	const mins = Math.floor(diff / 60_000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	return new Date(iso).toLocaleDateString();
}

const PROVIDER_ICONS = {
	anthropic: SiAnthropic,
	openai: SiOpenai,
	google: SiGooglegemini,
} as const;

const CHAT_MODELS = [
	{
		id: "anthropic/claude-sonnet-4.6",
		label: "Claude Sonnet 4.6",
		provider: "anthropic",
	},
	{
		id: "anthropic/claude-haiku-4.5",
		label: "Claude Haiku 4.5",
		provider: "anthropic",
	},
	{
		id: "anthropic/claude-opus-4.6",
		label: "Claude Opus 4.6",
		provider: "anthropic",
	},
	{ id: "openai/gpt-5.4", label: "GPT-5.4", provider: "openai" },
	{ id: "openai/gpt-5.4-pro", label: "GPT-5.4 Pro", provider: "openai" },
	{
		id: "google/gemini-3.1-flash-lite-preview",
		label: "Gemini 3.1 Flash Lite",
		provider: "google",
	},
	{
		id: "google/gemini-3.1-pro-preview",
		label: "Gemini 3.1 Pro",
		provider: "google",
	},
] as const;

const DEFAULT_MODEL = CHAT_MODELS[0].id;

const SUGGESTED_PROMPTS = [
	"What are my recent projects?",
	"Summarize this project",
	"What do my sources disagree on?",
	"Search my library for...",
];

export function ChatSidebar({
	isOpen,
	onClose,
}: ChatSidebarProps): ReactElement | null {
	const { data: session } = useSession();
	const userId = session?.user?.id;
	const pageContext = usePageContext();
	const {
		threadId,
		threadContext,
		initialMessages,
		isLoading: threadLoading,
		saveMessages,
		startNewThread,
		threads,
		threadsLoading,
		loadThreadList,
		switchThread,
	} = useChatThread(isOpen ? userId : undefined, pageContext);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const [inputValue, setInputValue] = useState("");
	const [showHistory, setShowHistory] = useState(false);
	const [selectedModel, setSelectedModel] = useState<string>(DEFAULT_MODEL);
	// CONTEXT: Track whether the user has scrolled away from the bottom
	// so we don't fight their scroll position during streaming.
	const userScrolledRef = useRef(false);

	// -- Resize logic --
	const MIN_WIDTH = 320;
	const DEFAULT_WIDTH = 400;
	const [sidebarWidth, setSidebarWidth] = useState(() => {
		const stored = localStorage.getItem("chat-sidebar-width");
		return stored ? Math.max(MIN_WIDTH, Number(stored)) : DEFAULT_WIDTH;
	});

	const handleResizeStart = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			const startX = e.clientX;
			const startWidth = sidebarWidth;

			const handleMouseMove = (moveEvent: MouseEvent) => {
				// Sidebar is on the right, so dragging left = wider
				const delta = startX - moveEvent.clientX;
				const maxWidth = Math.floor(window.innerWidth * 0.5);
				const newWidth = Math.min(
					maxWidth,
					Math.max(MIN_WIDTH, startWidth + delta),
				);
				setSidebarWidth(newWidth);
			};

			const handleMouseUp = () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
				// Persist final width once per drag instead of on every pixel
				setSidebarWidth((w) => {
					localStorage.setItem("chat-sidebar-width", String(w));
					return w;
				});
			};

			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		},
		[sidebarWidth],
	);

	// Clamp width on window resize so it never exceeds 50%
	useLayoutEffect(() => {
		const handleWindowResize = () => {
			const maxWidth = Math.floor(window.innerWidth * 0.5);
			setSidebarWidth((w) => Math.min(w, maxWidth));
		};
		window.addEventListener("resize", handleWindowResize);
		return () => window.removeEventListener("resize", handleWindowResize);
	}, []);

	// CONTEXT: Use refs so the transport body always has the latest values
	// without recreating the transport instance on every state change.
	const threadIdRef = useRef(threadId);
	threadIdRef.current = threadId;
	const contextRef = useRef(pageContext);
	contextRef.current = pageContext;
	const modelRef = useRef(selectedModel);
	modelRef.current = selectedModel;

	const transport = useMemo(
		() =>
			new DefaultChatTransport({
				api: CHAT_API_URL,
				headers: () => getChatHeaders(),
				body: () => ({
					threadId: threadIdRef.current,
					pageContext: contextRef.current,
					model: modelRef.current,
				}),
			}),
		[],
	);

	// Ref for saving — avoids onFinish closing over a stale saveMessages
	const saveRef = useRef(saveMessages);
	saveRef.current = saveMessages;

	const { messages, sendMessage, status, setMessages, error } = useChat({
		id: threadId ?? undefined,
		transport,
		messages: initialMessages,
		// CONTEXT: In AI SDK v6, multi-step tool loops are client-driven.
		// This tells useChat to automatically re-send after the server returns
		// tool call results, so the model can generate a follow-up response.
		sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
		onFinish: ({ messages: finalMessages }) => {
			saveRef.current(finalMessages);
		},
		onError: (err) => {
			console.error(
				"Chat error:",
				JSON.stringify({ error: err.message }),
			);
		},
	});

	const isStreaming = status === "streaming" || status === "submitted";

	// Scroll to bottom on new messages — use instant scroll during streaming
	// to avoid competing smooth-scroll animations on every token.
	// Skip if the user has scrolled up to read earlier content.
	useEffect(() => {
		if (userScrolledRef.current) return;
		messagesEndRef.current?.scrollIntoView({
			behavior: isStreaming ? "instant" : "smooth",
		});
	}, [messages, isStreaming]);

	// Reset the user-scrolled flag when streaming ends so the next
	// message auto-scrolls again.
	useEffect(() => {
		if (!isStreaming) userScrolledRef.current = false;
	}, [isStreaming]);

	const handleScroll = useCallback(() => {
		const el = scrollContainerRef.current;
		if (!el) return;
		// Consider "at bottom" if within 40px of the scroll end
		const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
		userScrolledRef.current = !atBottom;
	}, []);

	// Focus input when opened
	useEffect(() => {
		if (isOpen) {
			setTimeout(() => inputRef.current?.focus(), 100);
		}
	}, [isOpen]);

	const handleToggleHistory = useCallback(() => {
		const next = !showHistory;
		setShowHistory(next);
		if (next) loadThreadList();
	}, [showHistory, loadThreadList]);

	const handleSelectThread = useCallback(
		async (id: string) => {
			await switchThread(id);
			setMessages([]);
			setShowHistory(false);
		},
		[switchThread, setMessages],
	);

	const handleNewChat = useCallback(async () => {
		await startNewThread();
		setMessages([]);
		setInputValue("");
		setShowHistory(false);
		inputRef.current?.focus();
	}, [startNewThread, setMessages]);

	const handleSaveAsDocument = useCallback(
		(_content: string) => {
			const projectName = pageContext.projectName ?? "general";
			setInputValue(`Save the above as a document in ${projectName}`);
			inputRef.current?.focus();
		},
		[pageContext.projectName],
	);

	const drifted = hasContextDrifted(threadContext, pageContext);

	const handleSubmit = useCallback(
		(e?: React.FormEvent) => {
			e?.preventDefault();
			const text = inputValue.trim();
			if (!text || isStreaming) return;
			// CONTEXT: Context drift is handled server-side — the edge function
			// compares pageContext (sent in body) with the thread's stored context
			// and injects a drift note into the model messages if they differ.
			sendMessage({ text });
			setInputValue("");
			// Reset textarea height after clearing
			if (inputRef.current) inputRef.current.style.height = "auto";
		},
		[inputValue, isStreaming, sendMessage],
	);

	// Auto-resize textarea to fit content (up to ~4 lines)
	const autoResize = useCallback(() => {
		const el = inputRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${el.scrollHeight}px`;
	}, []);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSubmit();
			}
		},
		[handleSubmit],
	);

	if (!isOpen) return null;

	return (
		<div
			data-testid="chat-sidebar"
			className="bg-surface border-line fixed top-20 right-0 z-40 flex h-[calc(100vh-5rem)] flex-col border-l shadow-lg max-sm:w-full"
			style={{ width: `${sidebarWidth}px` }}
		>
			{/* Resize handle — hidden on mobile where sidebar is full-width */}
			<div
				onMouseDown={handleResizeStart}
				data-testid="chat-sidebar-resize"
				className="group absolute top-0 left-0 z-50 hidden h-full w-2 -translate-x-1/2 cursor-col-resize items-center justify-center sm:flex"
			>
				<div className="bg-fg-ghost group-hover:bg-accent h-8 w-1 rounded-full opacity-0 transition-opacity group-hover:opacity-100" />
			</div>
			{/* Header */}
			<div className="border-line flex shrink-0 items-center justify-between border-b px-4 py-3">
				<span className="text-fg text-sm font-medium">
					{showHistory ? "History" : "Chat"}
				</span>
				<div className="flex items-center gap-1">
					<button
						type="button"
						onClick={handleToggleHistory}
						className={`rounded p-1 transition-colors ${showHistory ? "text-accent" : "text-fg-ghost hover:text-fg"}`}
						title={showHistory ? "Back to chat" : "Thread history"}
						data-testid="chat-history-btn"
					>
						<FiClock className="text-base" />
					</button>
					{import.meta.env.DEV && threadId && !showHistory && (
						<button
							type="button"
							onClick={() =>
								window.open(
									`/dev/chat-debug?thread=${threadId}`,
									"_blank",
								)
							}
							className="text-fg-ghost hover:text-fg rounded p-1 transition-colors"
							title="Debug thread"
							data-testid="chat-debug-btn"
						>
							<FiTerminal className="text-base" />
						</button>
					)}
					<button
						type="button"
						onClick={handleNewChat}
						className="text-fg-ghost hover:text-fg rounded p-1 transition-colors"
						title="New chat"
						data-testid="chat-new-btn"
					>
						<FiPlus className="text-base" />
					</button>
					<button
						type="button"
						onClick={onClose}
						className="text-fg-ghost hover:text-fg rounded p-1 transition-colors"
						title="Close"
						data-testid="chat-close-btn"
					>
						<FiX className="text-base" />
					</button>
				</div>
			</div>

			{/* Messages / History */}
			<div
				ref={scrollContainerRef}
				onScroll={handleScroll}
				className="flex-1 overflow-y-auto px-4 py-3"
			>
				{showHistory ? (
					threadsLoading ? (
						<div className="text-fg-ghost flex h-full items-center justify-center text-sm">
							Loading...
						</div>
					) : threads.length === 0 ? (
						<div className="text-fg-ghost flex h-full items-center justify-center text-sm">
							No previous threads
						</div>
					) : (
						<div className="flex flex-col gap-1">
							{threads.map((thread) => (
								<button
									key={thread.id}
									type="button"
									onClick={() =>
										handleSelectThread(thread.id)
									}
									className={`rounded-lg px-3 py-2.5 text-left transition-colors ${
										thread.id === threadId
											? "bg-accent-wash text-accent"
											: "text-fg-secondary hover:bg-surface-raised hover:text-fg"
									}`}
									data-testid={`thread-item-${thread.id}`}
								>
									<div className="truncate text-sm">
										{thread.title ?? "Untitled"}
									</div>
									<div className="text-fg-ghost mt-0.5 text-[11px]">
										{formatRelativeTime(thread.updated_at)}
									</div>
								</button>
							))}
						</div>
					)
				) : threadLoading ? (
					<div className="text-fg-ghost flex h-full items-center justify-center text-sm">
						Loading...
					</div>
				) : messages.length === 0 ? (
					<div className="flex h-full flex-col items-center justify-center gap-4">
						<p className="text-fg-ghost text-sm">
							Ask anything about your knowledge base
						</p>
						<div className="flex flex-col gap-2">
							{SUGGESTED_PROMPTS.map((prompt) => (
								<button
									key={prompt}
									type="button"
									onClick={() => {
										setInputValue(prompt);
										inputRef.current?.focus();
									}}
									className="border-line text-fg-secondary hover:border-line-strong hover:text-fg rounded-lg border px-3 py-2 text-left text-xs transition-colors"
								>
									{prompt}
								</button>
							))}
						</div>
					</div>
				) : (
					<div
						data-testid="chat-messages"
						className="flex flex-col gap-3"
					>
						{messages.map((msg) => (
							<ChatMessage
								key={msg.id}
								message={msg}
								onSaveAsDocument={handleSaveAsDocument}
							/>
						))}
						{isStreaming && (
							<div
								data-testid="chat-streaming"
								className="text-fg-ghost flex items-center gap-2 text-sm"
							>
								<span className="bg-accent inline-block h-2 w-2 animate-pulse rounded-full" />
								Thinking...
							</div>
						)}
						{error && (
							<div className="rounded border border-red-200 px-3 py-2 text-xs text-red-500">
								{error.message}
							</div>
						)}
						<div ref={messagesEndRef} />
					</div>
				)}
			</div>

			{/* Input */}
			<div className="border-line shrink-0 border-t px-4 py-3">
				<div className="mb-1.5 flex items-center gap-1.5">
					<ChatContextPill context={pageContext} drifted={drifted} />
					<Menu.Root>
						<Menu.Trigger
							className="text-fg-ghost hover:text-fg flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] transition-colors"
							data-testid="chat-model-selector"
						>
							{CHAT_MODELS.find((m) => m.id === selectedModel)
								?.label ?? selectedModel}
							<FiChevronDown className="text-[10px]" />
						</Menu.Trigger>
						<Menu.Portal>
							<Menu.Positioner
								side="top"
								align="start"
								sideOffset={4}
								collisionPadding={16}
								className="z-50"
							>
								<Menu.Popup className="bg-surface-raised border-line w-[200px] rounded-lg border py-1 shadow-lg">
									{CHAT_MODELS.map((model) => {
										const Icon =
											PROVIDER_ICONS[model.provider];
										return (
											<Menu.Item
												key={model.id}
												onClick={() =>
													setSelectedModel(model.id)
												}
												className={`flex w-full cursor-default items-center gap-2 px-3 py-1.5 text-xs transition-colors outline-none ${
													model.id === selectedModel
														? "text-accent"
														: "text-fg-secondary hover:bg-skeleton hover:text-fg data-[highlighted]:bg-skeleton data-[highlighted]:text-fg"
												}`}
												data-testid={`chat-model-option-${model.id}`}
											>
												<Icon className="shrink-0 text-sm" />
												{model.label}
											</Menu.Item>
										);
									})}
								</Menu.Popup>
							</Menu.Positioner>
						</Menu.Portal>
					</Menu.Root>
				</div>
				<form
					onSubmit={handleSubmit}
					className="border-line focus-within:border-accent flex items-end gap-2 rounded-lg border px-3 py-2 transition-colors"
				>
					<textarea
						ref={inputRef}
						value={inputValue}
						onChange={(e) => {
							setInputValue(e.target.value);
							autoResize();
						}}
						onKeyDown={handleKeyDown}
						placeholder="Ask about your library..."
						rows={1}
						className="text-fg placeholder:text-fg-ghost max-h-24 flex-1 resize-none bg-transparent text-sm outline-none"
						data-testid="chat-input"
					/>
					<button
						type="submit"
						disabled={!inputValue.trim() || isStreaming}
						className="text-accent disabled:text-fg-ghost shrink-0 p-0.5 transition-colors"
						data-testid="chat-send-btn"
					>
						<FiSend className="text-base" />
					</button>
				</form>
				<div className="mt-1 flex items-center justify-end">
					<kbd className="text-fg-ghost text-[10px]">
						{navigator.platform?.includes("Mac")
							? "\u2318\u21E7K"
							: "Ctrl+Shift+K"}
					</kbd>
				</div>
			</div>
		</div>
	);
}
